/**
 * Admin nav config (DOS-388) — single source of truth for the /admin shell.
 *
 * Each entry describes one admin section: how to route to it, how to gate it,
 * and how to render it in the shell sub-nav + landing grid. AdminShell filters
 * this list against the current user's role/capabilities.
 *
 * Access rule per entry:
 *   - `role` present  → user must satisfy hasRole(role) (superadmin-only surfaces)
 *   - `anyCapability` → user must have any one of the listed capabilities
 *   - (both may be set for OR semantics — role OR one of the caps)
 */

export const ADMIN_NAV = [
  {
    path: '/admin/users',
    label: 'Users',
    description: 'Manage organization members, roles, and personas.',
    anyCapability: ['manage_users', 'manage_users_own_location'],
  },
  {
    path: '/admin/locations',
    label: 'Locations',
    description: 'Manage rooftops and per-location assignments.',
    anyCapability: ['edit_locations', 'edit_location_own'],
  },
  {
    path: '/admin/skills',
    label: 'Skills',
    description: 'Enable, disable, or configure org-level skills.',
    anyCapability: ['manage_skills'],
  },
  {
    path: '/admin/models',
    label: 'Models',
    description: 'Curate the platform model catalog, defaults, and rates.',
    role: 'superadmin',
  },
  {
    path: '/admin/ai-allowance',
    label: 'AI allowance',
    description: 'Monthly AI spend limits per org and platform-wide.',
    role: 'superadmin',
  },
  {
    path: '/admin/display',
    label: 'Chat display',
    description: 'What chat users see of the agent\u2019s work (thinking).',
    role: 'superadmin',
  },
  {
    path: '/admin/skills/platform',
    label: 'Platform Skills',
    description: 'Publish and manage universal skills for every dealer group.',
    role: 'superadmin',
  },
  {
    path: '/admin/context',
    label: 'Context',
    description: 'Edit dealership context files powering AI responses.',
    anyCapability: ['edit_context', 'edit_context_own_location'],
  },
  {
    path: '/admin/srm',
    label: 'SRM Credentials',
    description: 'Manage SRM integration credentials for this org.',
    anyCapability: ['manage_srm_credentials'],
  },
  {
    path: '/admin/new-org',
    label: 'New Organization',
    description: 'Onboard a new dealer group via the wizard.',
    anyCapability: ['create_org'],
  },
  {
    path: '/admin/onboarding',
    label: 'Onboarding',
    description: 'Health snapshot for every dealer group — completion state, section coverage, freshness.',
    role: 'superadmin',
  },
  {
    path: '/admin/usage',
    label: 'Usage & Cost',
    description: 'Claude API spend per dealer, org, skill, agent, workflow, or user.',
    role: 'superadmin',
  },
  {
    path: '/admin/signups',
    label: 'Signup Queue',
    description: 'Approve or reject pending user signups.',
    role: 'superadmin',
  },
  {
    path: '/admin/domains',
    label: 'Domain Allowlist',
    description: 'Manage trusted and blocked email domains.',
    role: 'superadmin',
  },
];

/**
 * Filter ADMIN_NAV to entries the user has access to.
 *   hasRole(entry.role) OR hasAnyCapability(...entry.anyCapability)
 */
export function filterAdminNav(user, entries = ADMIN_NAV) {
  const { hasRole, hasAnyCapability } = user;
  return entries.filter(e => {
    if (e.role && hasRole(e.role)) return true;
    if (e.anyCapability && hasAnyCapability(...e.anyCapability)) return true;
    return false;
  });
}

/**
 * Does the user have access to any admin surface at all? Used by the
 * `/admin` route guard and the landing page's empty-state.
 */
export function hasAnyAdminAccess(user) {
  return filterAdminNav(user).length > 0;
}
